import {
  PresetManager,
  ResourceInfoColumnConfig,
  SchedulerConfig,
  SchedulerFeaturesConfigType,
  ViewPresetConfig
} from "@bryntum/scheduler-thin";
import { WorkMode } from "@app/models/rest/resource";
import { FilterConfig } from "@bryntum/grid-thin";
import {
  CollectionFilterConfig,
  FieldFilterPickerConfig,
  Store as BryntumStore
} from "@bryntum/core-thin";
import { ResourceAvailability_DetailProjection } from "@app/models/rest/resource-availability";

export class ScheduleDefaults {
  private static skillsStore = new BryntumStore({
    fields: ["id", "name"],
    data: [{ id: 7, name: "To Attend" }] // Keep your test data initially
  });

  private static zonesStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static teamsStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static resourcesStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  public static setFilterData(
    skills: any[],
    zones: any[],
    teams?: any[],
    resources?: any[]
  ) {
    // Ensure we have valid arrays and filter out any invalid entries
    const validSkills = (skills || []).filter(
      (skill) => skill && skill.id && skill.description
    );
    const validZones = (zones || []).filter(
      (zone) => zone && zone.id && zone.name
    );
    const validTeams = (teams || []).filter(
      (team) => team && team.id && team.name
    );
    const validResources = (resources || []).filter(
      (resource) => resource && resource.id && resource.name
    );

    const skillData = validSkills.map((skill) => ({
      id: skill.id,
      name: skill.description || "Unknown Skill"
    }));
    const zoneData = validZones.map((zone) => ({
      id: zone.id,
      name: zone.name || "Unknown Zone"
    }));

    // Clear and add new data to stores
    this.skillsStore.removeAll();
    if (skillData.length > 0) {
      this.skillsStore.add(skillData);
    }

    this.zonesStore.removeAll();
    if (zoneData.length > 0) {
      this.zonesStore.add(zoneData);
    }

    // Handle teams if provided
    if (validTeams.length > 0) {
      const teamData = validTeams.map((team) => ({
        id: team.id,
        name: team.name || "Unknown Team"
      }));
      this.teamsStore.removeAll();
      this.teamsStore.add(teamData);
      console.log("Updated teams store with:", teamData);
    }

    // Handle resources if provided
    if (validResources.length > 0) {
      const resourceData = validResources.map((resource) => ({
        id: resource.id,
        name: resource.name || "Unknown Resource"
      }));
      this.resourcesStore.removeAll();
      this.resourcesStore.add(resourceData);
      console.log("Updated resources store with:", resourceData);
    }

    console.log("Updated skills store with:", skillData);
    console.log("Updated zones store with:", zoneData);
  }

  public static getSkillsStore() {
    return this.skillsStore;
  }

  public static getZonesStore() {
    return this.zonesStore;
  }

  public static getTeamsStore() {
    return this.teamsStore;
  }

  public static getResourcesStore() {
    return this.resourcesStore;
  }
  public static DEFAULT_SETTINGS: Partial<SchedulerConfig> = {
    barMargin: 2,
    rowHeight: 50,
    columnLines: true,
    enableEventAnimations: true,
    // Must match the diary view's stateId so filters carry across the two
    // screens (#1448). If a reset is needed, bump both together.
    // v9: reset stale persisted state that broke diary tile rendering (#1516).
    stateId: "planner-view-v9",
    eventSelectionDisabled: true,
    multiEventSelect: false,
    useInitialAnimation: true,
    scrollable: true,
    forceFit: true,
    autoHeight: false,
    zoomOnMouseWheel: false,
    zoomOnTimeAxisDoubleClick: false,
    viewPreset: "hour",
    cls: "sch-container",
    // eventStyle: "margin: 0;",
    style: "height: calc(100vh - 88px);",
    resourceTimeRangeRenderer({ resourceTimeRangeRecord }) {
      let template = `<h1 style="line-height:1.1; font-size: 16px; font-weight: bold; margin: 0 4px;">${
        resourceTimeRangeRecord.name ?? "&nbsp;"
      }</h1>`;
      if (resourceTimeRangeRecord.get("entity")) {
        const entity: ResourceAvailability_DetailProjection =
          resourceTimeRangeRecord.get("entity");
        if (entity.description) {
          template += `<div style="margin: 0 4px; font-size: 12px; max-height: 28px; overflow:hidden;">${entity.description}</div>`;
        }
      }
      return template;
    },
    columns: [
      {
        id: "resource",
        type: "resourceInfo",
        locked: true,
        draggable: false,
        text: "Resource",
        showEventCount: false,
        showImage: true,
        editor: false,
        showRole: false,
        showMeta: (record) => {
          return record.get("activityDescription") ?? "&nbsp;";
        },
        width: 240,
        htmlEncode: false,
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by resources...",
            store: ScheduleDefaults.resourcesStore
          },
          // Custom filtering function to handle the resource structure
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            // Check if the resource's id matches the selected filter values
            return value.includes(record.id);
          }
        }
      } as unknown as ResourceInfoColumnConfig,
      {
        id: "team",
        text: "Team",
        width: 200,
        field: "team",
        hidden: true,
        renderer: ({ record }) => {
          const entity = record.get("entity");
          if (entity && entity.team) {
            return entity.team.name;
          }
          return "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by teams...",
            store: ScheduleDefaults.teamsStore
          },
          // Custom filtering function to handle the team structure
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            const entity = record.get("entity");
            if (!entity || !entity.team) return false;

            // Check if the resource's team matches the selected filter values
            return value.includes(entity.team.id);
          }
        }
      },
      {
        id: "skills",
        width: 360,
        text: "Skills",
        field: "skillText",
        hidden: true,
        // skillText is precomputed for the current view range — only skill
        // assignments active in the visible time span are included (#1558).
        renderer: ({ record }) => record.get("skillText") || "",
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by skills...",
            store: ScheduleDefaults.skillsStore
          },
          // Match against the skills active for the current view range
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            const skillIds = record.get("effectiveSkillIds");
            if (!skillIds) return false;

            return skillIds.some((skillId) => value.includes(skillId));
          }
        }
      },
      {
        id: "zones",
        text: "Zones",
        field: "zoneText",
        hidden: true,
        width: 200,
        // zoneText is precomputed for the current view range — only zone
        // assignments active in the visible time span are included (#1558).
        renderer: ({ record }) => record.get("zoneText") || "",
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by zones...",
            store: ScheduleDefaults.zonesStore
          },
          // Match against the zones active for the current view range
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            const zoneIds = record.get("effectiveZoneIds");
            if (!zoneIds) return false;

            return zoneIds.some((zoneId) => value.includes(zoneId));
          }
        }
      },
      {
        id: "workMode",
        text: "Mode",
        field: "workMode",
        hidden: true,
        width: 50,
        renderer: ({ record }) =>
          record.workMode && record.workMode === WorkMode.Offline
            ? "Offline"
            : "Online"
      }
    ],
    features: null
  };
  public static createFilterConfig(): Partial<FilterConfig> {
    return {
      pickerConfig: {
        showAddFilterButton: false,
        dateFormat: "DD/MM/YYYY",

        canDeleteFilter: () => {
          return false;
        },
        getFieldFilterPickerConfig: (
          collectionFilterConfig: CollectionFilterConfig
        ): Partial<FieldFilterPickerConfig> => {
          console.log("getFieldFilterPickerConfig", collectionFilterConfig);
          switch (collectionFilterConfig.property) {
            case "skillsList": {
              console.log("Setting up skillsList filter");
              const fixedFieldResult: Partial<FieldFilterPickerConfig> = {
                operatorLocked: true,
                propertyLocked: true,
                operators: {
                  array: [
                    {
                      value: "includes",
                      text: "Filter by skills",
                      argCount: 1
                    }
                  ]
                }
              };
              return fixedFieldResult;
            }
            case "zonesList": {
              console.log("Setting up zonesList filter");
              const fixedFieldResult: Partial<FieldFilterPickerConfig> = {
                operatorLocked: true,
                propertyLocked: true,
                operators: {
                  array: [
                    {
                      value: "includes",
                      text: "Filter by zones",
                      argCount: 1
                    }
                  ]
                }
              };
              return fixedFieldResult;
            }
            case "team": {
              const fixedFieldResult: Partial<FieldFilterPickerConfig> = {
                operatorLocked: true,
                propertyLocked: true,
                operators: {
                  string: [
                    {
                      value: "isIncludedIn",
                      text: "Filter by team",
                      argCount: 1
                    }
                  ]
                }
              };
              return fixedFieldResult;
            }
          }
          return {};
        }
      }
    };
  }

  public static createDefaultFeatures(): Partial<SchedulerFeaturesConfigType> {
    return {
      // eventDrag: true,
      rowCopyPaste: false,
      eventCopyPaste: false,
      eventDragCreate: false,
      scheduleMenu: {
        items: {
          addEvent: false
          // custom: {
          //   text: "No menu items available",
          //   disabled: true
          // },
          // editShift : {
          //   text : 'Edit Shift',
          //   icon : 'b-fa b-fa-fw b-fa-flag',
          //
          //   onItem({date, resourceRecord}) {
          //     // @ts-ignore
          //     const intersectedTimeRanges = resourceRecord.timeRanges.filter((timeRange) => DateHelper.betweenLesserEqual(date, timeRange.startDate, timeRange.endDate) );
          //     console.log("Time ranges", intersectedTimeRanges);
          //   }
          // }
        }
      },
      cellMenu: {
        items: {
          removeRow: false
        }
      },
      // eventContextMenu: {
      //   items: {
      //     deleteEvent: false,
      //     unassignEvent: false
      //   }
      // },
      eventEdit: false,
      eventFilter: true,
      eventResize: true,
      columnResize: true,
      regionResize: true,
      scheduleTooltip: false,
      // eventSelect: false,
      timeRanges: { showHeaderElements: true, showCurrentTimeLine: true },
      resourceTimeRanges: {
        enableMouseEvents: true
      },
      cellEdit: false,
      filter: ScheduleDefaults.createFilterConfig(),
      // headerContextMenu: false,
      timeAxisHeaderMenu: {
        items: {
          zoomLevel: false,
          dateRange: false
        }
      },
      eventDrag: {
        constrainDragToResource: true
      }
    };
  }

  public static Create(config, features): any {
    let mergedConfig: any = ScheduleDefaults.DEFAULT_SETTINGS;
    mergedConfig = Object.assign(mergedConfig, config);
    mergedConfig.features = ScheduleDefaults.createDefaultFeatures();
    mergedConfig.features = Object.assign(mergedConfig.features, features);

    const preset: Partial<ViewPresetConfig> = {
      name: "weekAndDay",
      displayDateFormat: "HH:mm",
      shiftIncrement: 1,
      shiftUnit: "day",
      mainHeaderLevel: 1,
      timeResolution: {
        unit: "minute",
        increment: 5
      },
      columnLinesFor: 1,
      headers: [
        {
          unit: "hour",
          headerCellCls: "minute-tick",
          dateFormat: "HH:mm"
        },
        {
          unit: "minute",
          increment: 15,
          dateFormat: "",
          headerCellCls: "minute-tick"
        }
      ]
    };
    PresetManager.registerPreset("hour", preset);

    return mergedConfig;
  }
}
